import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'react-hot-toast';
import cartService from '@/services/api/cart';
import { Cart, CartItem, AddToCartData } from '@/services/types/domain';

interface CartState {
    // Estado
    cart: Cart | null;
    isLoading: boolean;
    itemsCount: number;

    // Acciones
    fetchCart: () => Promise<Cart>;
    //fetchCartCount: () => Promise<void>;
    fetchCartCount: () => Promise<number>;
    //addItem: (data: AddToCartData) => Promise<void>;
    addItem: (data: AddToCartData) => Promise<boolean>;
    updateItemQuantity: (itemId: number, quantity: number) => Promise<void>;
    removeItem: (itemId: number) => Promise<void>;
    clearCart: () => Promise<void>;
    //applyCoupon: (couponCode: string) => Promise<void>;
    applyCoupon: (couponCode: string) => Promise<boolean>;
    removeCoupon: () => Promise<void>;
    checkStock: (productId: number, variantId?: number) => Promise<boolean>;

    // Utilidades mejoradas
    getItemById: (itemId: number) => CartItem | undefined;
    getItemByProduct: (productId: number, variantId?: number) => CartItem | undefined;
    getTotalItems: () => number;
    getSubtotal: () => number;
    getTotal: () => number;
    getDiscountAmount: () => number;
    getShippingCost: () => number;
    getItems: () => CartItem[];
    getProductIds: () => number[];
    hasProduct: (productId: number, variantId?: number) => boolean;
    getProductQuantity: (productId: number, variantId?: number) => number;

    // Estado interno
    setLoading: (loading: boolean) => void;
    setCart: (cart: Cart | null) => void;
    setItemsCount: (count: number) => void;
}

export const useCartStore = create<CartState>()(
    persist(
        (set, get) => ({
            // Estado inicial
            cart: null,
            isLoading: false,
            itemsCount: 0,

            // Acciones principales (mejoradas con manejo de errores más robusto)
            /*fetchCart: async () => {
                //console.log("esto entrando aqui")
                try {
                    set({ isLoading: true });
                    const cart = await cartService.getCart();
                    set({
                        cart,
                        itemsCount: cart?.items?.length || 0,
                        isLoading: false
                    });
                } catch (error: any) {
                    set({ isLoading: false });
                    console.error('Error fetching cart:', error);

                    if (error.response?.status === 404) {
                        set({ cart: null, itemsCount: 0 });
                    } else {
                        toast.error('Error al cargar el carrito');
                    }
                    throw error;
                }
            },*/
            fetchCart: async () => {
                try {
                    set({ isLoading: true });
                    const response = await cartService.getCart();
                    //console.log("respon", response);
                    if (!response.success || !response.data) {
                        throw new Error(response.message || 'Error al obtener el carrito');
                    }

                    set({
                        cart: response.data, // Usamos directamente la respuesta
                        itemsCount: response.data.item_count,
                        isLoading: false
                    });
                    return response.data;
                } catch (error: any) {
                    set({ isLoading: false });
                    console.error('Error fetching cart:', error);

                    if (error.response?.status === 404) {
                        set({ cart: null, itemsCount: 0 });
                    } else {
                        toast.error(error.message || 'Error al cargar el carrito');
                    }
                    throw error;
                }
            },

            fetchCartCount: async () => {
                try {
                    const count = await cartService.getCartCount();
                    set({ itemsCount: count ?? 0 });
                    return count;
                } catch (error: any) {
                    console.error('Error fetching cart count:', error);
                    set({ itemsCount: 0 });
                    throw error;
                }
            },

            addItem: async (data: AddToCartData) => {
                try {
                    set({ isLoading: true });

                    // Verificar stock
                    const stockCheck = await get().checkStock(data.product_id, data.variant_id);
                    if (!stockCheck) {
                        toast.error('Stock insuficiente para este producto');
                        set({ isLoading: false });
                        return false;
                    }

                    await cartService.addItem(data);
                    await get().fetchCart();

                    toast.success('Producto agregado al carrito');
                    return true;
                } catch (error: any) {
                    set({ isLoading: false });
                    const message = error.response?.data?.message || 'Error al agregar producto';
                    toast.error(message);
                    throw error;
                }
            },

            /*updateItemQuantity: async (itemId: number, quantity: number) => {
                
                if (quantity <= 0) {
                    await get().removeItem(itemId);
                    return;
                }

                try {
                    set({ isLoading: true });
                    await cartService.updateItem(itemId, quantity);
                    await get().fetchCart();
                } catch (error: any) {
                    set({ isLoading: false });
                    const message = error.response?.data?.message || 'Error al actualizar cantidad';
                    toast.error(message);
                    throw error;
                }
            },*/
            updateItemQuantity: async (itemId: number, quantity: number) => {
                if (quantity <= 0) {
                    await get().removeItem(itemId);
                    return;
                }

                try {
                    set({ isLoading: true });

                    // 1. Primero verificar el stock disponible
                    const item = get().getItemById(itemId);
                    if (!item) {
                        toast.error('Producto no encontrado en el carrito');
                        return;
                    }

                    // 2. Llamar al servicio para actualizar
                    await cartService.updateItem(itemId, quantity);

                    // 3. Si tiene éxito, actualizar el carrito
                    await get().fetchCart();

                } catch (error: any) {
                    set({ isLoading: false });

                    // Manejo específico para error 409 (Stock insuficiente)
                    //console.log("error", error)
                    //return
                    if (error.response?.status === 409) {
                        const mensaje = error.response?.data?.error || "Inpesperador";
                        toast.error(`Error:  ${mensaje}`);

                        // Actualizar a la cantidad máxima disponible
                        /*if (availableStock > 0) {
                            await get().updateItemQuantity(itemId, availableStock);
                        } else {
                            await get().removeItem(itemId);
                        }*/
                        return;
                    }

                    // Manejo de otros errores
                    const message = error.response?.data?.error ||
                        error.response?.data?.message ||
                        'Error al actualizar la cantidad';
                    toast.error(message);
                    console.error('Error updating item quantity:', error);
                }
            },
            removeItem: async (itemId: number) => {
                try {
                    set({ isLoading: true });
                    await cartService.removeItem(itemId);
                    await get().fetchCart();
                    toast.success('Producto removido del carrito');
                } catch (error: any) {
                    set({ isLoading: false });
                    const message = error.response?.data?.message || 'Error al remover producto';
                    toast.error(message);
                    throw error;
                }
            },

            clearCart: async () => {
                try {
                    set({ isLoading: true });
                    await cartService.clearCart();
                    set({ cart: null, itemsCount: 0, isLoading: false });
                    toast.success('Carrito vaciado');
                } catch (error: any) {
                    set({ isLoading: false });
                    const message = error.response?.data?.message || 'Error al vaciar carrito';
                    toast.error(message);
                    throw error;
                }
            },

            applyCoupon: async (couponCode: string) => {
                try {
                    set({ isLoading: true });
                    const updatedCart = await cartService.applyCoupon(couponCode);
                    set({ cart: updatedCart, isLoading: false });
                    toast.success('Cupón aplicado correctamente');
                    return true;
                } catch (error: any) {
                    set({ isLoading: false });
                    const message = error.response?.data?.message || 'Error al aplicar cupón';
                    toast.error(message);
                    throw error;
                }
            },

            removeCoupon: async () => {
                try {
                    set({ isLoading: true });
                    const updatedCart = await cartService.removeCoupon();
                    set({ cart: updatedCart, isLoading: false });
                    toast.success('Cupón removido');
                } catch (error: any) {
                    set({ isLoading: false });
                    const message = error.response?.data?.message || 'Error al remover cupón';
                    toast.error(message);
                    throw error;
                }
            },

            checkStock: async (productId: number, variantId?: number) => {
                try {
                    const stockCheck = await cartService.checkStock(productId, variantId);
                    return stockCheck.available && stockCheck.stock_quantity > 0;
                } catch (error) {
                    console.error('Error checking stock:', error);
                    return false;
                }
            },

            // Utilidades mejoradas
            getItemById: (itemId: number) => {
                return get().cart?.items.find(item => item.id === itemId);
            },

            getItemByProduct: (productId: number, variantId?: number) => {
                return get().cart?.items.find(item =>
                    item.product_id === productId &&
                    (variantId ? item.variant_id === variantId : !item.variant_id)
                );
            },

            getTotalItems: () => {
                return get().cart?.items.reduce((total, item) => total + item.quantity, 0) || 0;
            },

            getSubtotal: () => {
                return get().cart?.totals.subtotal || 0;
            },

            getTotal: () => {
                return get().cart?.totals.total || 0;
            },

            getDiscountAmount: () => {
                return get().cart?.totals.discount || 0;
            },

            getShippingCost: () => {
                return get().cart?.totals.shipping || 0;
            },

            getItems: () => {
                return get().cart?.items || [];
            },

            getProductIds: () => {
                return get().cart?.items.map(item => item.product_id) || [];
            },

            hasProduct: (productId: number, variantId?: number) => {
                return !!get().getItemByProduct(productId, variantId);
            },

            getProductQuantity: (productId: number, variantId?: number) => {
                const item = get().getItemByProduct(productId, variantId);
                return item?.quantity || 0;
            },

            // Métodos de utilidad
            setLoading: (loading: boolean) => set({ isLoading: loading }),
            setCart: (cart: Cart | null) => set({ cart }),
            setItemsCount: (count: number) => set({ itemsCount: count }),
        }),
        {
            name: 'cart-storage',
            partialize: (state) => ({
                itemsCount: state.itemsCount,
            }),
        }
    )
);

// Hook personalizado mejorado para usar el carrito
export const useCart = () => {
    const store = useCartStore();

    return {
        // Estado
        items: store.getItems(),
        total: store.getTotal(),
        subtotal: store.getSubtotal(),
        discount: store.getDiscountAmount(),
        shipping: store.getShippingCost(),
        itemCount: store.getTotalItems(),
        isLoading: store.isLoading,
        cart: store.cart,

        // Acciones básicas
        addProduct: async (productId: number, quantity: number = 1, variantId?: number) => {
            return await store.addItem({ product_id: productId, quantity, variant_id: variantId });
        },

        updateQuantity: async (productId: number, quantity: number, variantId?: number) => {
            const item = store.getItemByProduct(productId, variantId);
            //console.log("item", item, productId, variantId, quantity);
            if (item) {
                return await store.updateItemQuantity(item.id, quantity);
            }
            return false;
        },

        removeItem: async (productId: number, variantId?: number) => {
            const item = store.getItemByProduct(productId, variantId);
            if (item) {
                return await store.removeItem(item.id);
            }
            return false;
        },

        incrementItem: async (productId: number, variantId?: number) => {
            const item = store.getItemByProduct(productId, variantId);
            if (item) {
                return await store.updateItemQuantity(item.id, item.quantity + 1);
            }
            return false;
        },

        decrementItem: async (productId: number, variantId?: number) => {
            const item = store.getItemByProduct(productId, variantId);
            if (item) {
                return await store.updateItemQuantity(item.id, item.quantity - 1);
            }
            return false;
        },

        clearCart: store.clearCart,
        applyCoupon: store.applyCoupon,
        removeCoupon: store.removeCoupon,
        checkStock: store.checkStock,

        // Utilidades
        hasProduct: store.hasProduct,
        getProductQuantity: store.getProductQuantity,
        getProductIds: store.getProductIds,
        refreshCart: store.fetchCart,
        refreshCount: store.fetchCartCount,
        fetchCartCount: store.fetchCartCount,
        // Métodos directos del store (para casos especiales)
        _store: store
    };
};

export default useCartStore;